/**
 * REMOVED ROWS MANAGER
 * Manages temporarily removed rows per sheet (frontend only)
 * These rows are filtered out when loading data but remain in the database
 */

/**
 * Get storage key for removed rows for a specific sheet
 */
export const getRemovedRowsStorageKey = (sheetName: string, siteCode: string, userId?: string | number): string => {
  const userPart = userId ? `_user_${userId}` : '';
  return `removedRows_${sheetName}_${siteCode}${userPart}`;
};

/**
 * Get list of removed row keys for a sheet
 */
export const getRemovedRows = (sheetName: string, siteCode: string, userId?: string | number): string[] => {
  try {
    const storageKey = getRemovedRowsStorageKey(sheetName, siteCode, userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
}
  return [];
};

/**
 * Add a row key to the removed rows list
 */
export const addRemovedRow = (sheetName: string, siteCode: string, rowKey: string, userId?: string | number): void => {
  try {
    const storageKey = getRemovedRowsStorageKey(sheetName, siteCode, userId);
    const removedRows = getRemovedRows(sheetName, siteCode, userId);
    
    if (!removedRows.includes(rowKey)) {
      removedRows.push(rowKey);
      localStorage.setItem(storageKey, JSON.stringify(removedRows));
}
  } catch (error) {
}
};

/**
 * Remove a row key from the removed rows list (restore it)
 */
export const removeRemovedRow = (sheetName: string, siteCode: string, rowKey: string, userId?: string | number): void => {
  try {
    const storageKey = getRemovedRowsStorageKey(sheetName, siteCode, userId);
    const removedRows = getRemovedRows(sheetName, siteCode, userId);
    const filtered = removedRows.filter(key => key !== rowKey);
    
    if (filtered.length !== removedRows.length) {
      localStorage.setItem(storageKey, JSON.stringify(filtered));
}
  } catch (error) {
}
};

/**
 * Clear all removed rows for a sheet
 */
export const clearRemovedRows = (sheetName: string, siteCode: string, userId?: string | number): void => {
  try {
    const storageKey = getRemovedRowsStorageKey(sheetName, siteCode, userId);
    localStorage.removeItem(storageKey);
} catch (error) {
}
};

/**
 * Sync removed rows with backend data - remove any removed rows that exist in backend
 * This ensures backend data is always visible (Excel-like behavior)
 */
export const syncRemovedRowsWithBackend = (
  backendRowKeys: string[],
  sheetName: string,
  siteCode: string,
  userId?: string | number
): void => {
  try {
    const removedRows = getRemovedRows(sheetName, siteCode, userId);
    if (removedRows.length === 0) {
      return;
    }
    
    const backendKeysSet = new Set(backendRowKeys);
    const restoredRows: string[] = [];
    
    removedRows.forEach(removedKey => {
      // Check if removed key exists in backend (exact match or date match for composite keys)
      const existsInBackend = backendRowKeys.some(backendKey => {
        if (backendKey === removedKey) {
          return true;
        }
        // For composite keys, check date parts
        const backendDatePart = backendKey.includes('_') ? backendKey.split('_')[0] : backendKey;
        const removedDatePart = removedKey.includes('_') ? removedKey.split('_')[0] : removedKey;
        return backendDatePart === removedDatePart;
      });
      
      if (existsInBackend) {
        removeRemovedRow(sheetName, siteCode, removedKey, userId);
        restoredRows.push(removedKey);
      }
    });
    
    if (restoredRows.length > 0) {
}
  } catch (error) {
}
};

/**
 * Filter out removed rows from data and rowKeys
 * IMPORTANT: By default, temporarily removed rows are NOT restored from backend data.
 * This ensures temporary removals persist until page refresh.
 * Set restoreBackendRows: true to restore backend rows (for initial load only).
 */
export const filterRemovedRows = <T extends Record<string, any>>(
  data: T,
  rowKeys: string[],
  sheetName: string,
  siteCode: string,
  userId?: string | number,
  options?: { restoreBackendRows?: boolean }
): { data: T; rowKeys: string[]; restoredRows?: string[] } => {
  const removedRows = getRemovedRows(sheetName, siteCode, userId);
  // Default to false - don't restore backend rows (temporary removals persist until refresh)
  const shouldRestoreBackendRows = options?.restoreBackendRows === true;
  
  if (removedRows.length === 0) {
    return { data, rowKeys };
  }
  
  const filteredData = { ...data };
  const filteredKeys: string[] = [];
  const restoredRows: string[] = [];
  const rowsToRestore: string[] = [];
  
  rowKeys.forEach(key => {
    // For composite keys (like date_id), check if the date part matches
    const datePart = key.includes('_') ? key.split('_')[0] : key;
    const isRemoved = removedRows.some(removedKey => {
      // Check exact match first
      if (key === removedKey) {
        return true;
      }
      // For composite keys, check if date parts match
      const removedDatePart = removedKey.includes('_') ? removedKey.split('_')[0] : removedKey;
      // Match if date parts are the same (handles cases where ID might differ)
      if (datePart === removedDatePart) {
        return true;
      }
      return false;
    });
    
    // Only restore backend rows if explicitly requested (for initial load)
    if (isRemoved && shouldRestoreBackendRows) {
      // Row exists in backend and restoration is explicitly requested, restore it
      filteredKeys.push(key);
      restoredRows.push(key);
      rowsToRestore.push(key);
} else if (!isRemoved) {
      // Row is not removed, include it
      filteredKeys.push(key);
    } else {
      // Row is removed - filter it out (even if it exists in backend for temporary removals)
      delete filteredData[key];
}
  });
  
  // Restore rows from removed list (remove from localStorage) only if explicitly requested
  if (rowsToRestore.length > 0 && shouldRestoreBackendRows) {
    rowsToRestore.forEach(key => {
      removeRemovedRow(sheetName, siteCode, key, userId);
    });
}
  
  if (filteredKeys.length !== rowKeys.length && restoredRows.length === 0) {
} else if (removedRows.length > 0 && restoredRows.length === 0) {
}
  
  return { data: filteredData, rowKeys: filteredKeys, restoredRows };
};


